import { useState, useMemo } from 'react';
import {
  useGroups,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
} from '../../hooks/useGroups';
import {
  useAdditionals,
  useCreateAdditional,
  useUpdateAdditional,
  useDeleteAdditional,
} from '../../hooks/useAdditionals';
import {
  useScoreCoefficients,
  useAddScoreCoefficient,
  useUpdateScoreCoefficient,
  useDeleteScoreCoefficient,
} from '../../hooks/useScoreCoefficient';
import {
  usePreAdjudicationReasons,
  useCreatePreAdjudicationReason,
  useUpdatePreAdjudicationReason,
  useDeletePreAdjudicationReason,
} from '../../hooks/usePreAdjudicationReasons';
import {
  useConstructionTypes,
  useCreateConstructionType,
  useUpdateConstructionType,
  useDeleteConstructionType,
} from '../../hooks/useConstructionTypes';
import { useLegalEntities } from '../../hooks/useLegalEntities';
import { useFees, useCreateFee, useUpdateFee, useDeleteFee } from '../../hooks/useFees';
import {
  useFeeStructures,
  useCreateFeeStructure,
  useUpdateFeeStructure,
  useDeleteFeeStructure,
} from '../../hooks/useFeeStructures';
import {
  usePaymentMethods,
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
  useDeletePaymentMethod,
} from '../../hooks/usePaymentMethods';
import {
  usePaymentMethodDetails,
  useCreatePaymentMethodDetail,
  useUpdatePaymentMethodDetail,
  useDeletePaymentMethodDetail,
} from '../../hooks/usePaymentMethodDetails';

/**
 * Hook para manejar la lógica del diálogo de edición de plan
 * Gestiona todas las tabs y sus respectivos estados
 */
export const usePlanEditDialog = ({
  selectedPlan,
  activeTab,
  setActiveTab,
}) => {

  // Estados locales para cada tab
  const [selectedItems, setSelectedItems] = useState({
    group: {},
    additional: {},
    scoreCoefficient: {},
    preAdjReason: {},
    constructionType: {},
    fee: {},
    feeStructure: {},
    paymentMethod: {},
    paymentMethodDetail: {},
  });

  const [dialogStates, setDialogStates] = useState({
    group: false,
    additional: false,
    scoreCoefficient: false,
    preAdjReason: false,
    constructionType: false,
    fee: false,
    feeStructure: false,
    paymentMethod: false,
    paymentMethodDetail: false,
  });

  const [editModes, setEditModes] = useState({
    group: false,
    additional: false,
    scoreCoefficient: false,
    preAdjReason: false,
    constructionType: false,
    fee: false,
    feeStructure: false,
    paymentMethod: false,
    paymentMethodDetail: false,
  });

  // Queries para cada módulo
  const { data: groups = [] } = useGroups();
  const { data: additionals = [] } = useAdditionals();
  const { data: scoreCoefficients = [] } = useScoreCoefficients();
  const { data: preAdjReasons = [] } = usePreAdjudicationReasons();
  const { data: constructionTypes = [] } = useConstructionTypes();
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: fees = [] } = useFees();
  const { data: feeStructures = [] } = useFeeStructures();
  const { data: paymentMethods = [] } = usePaymentMethods();
  const { data: paymentMethodDetails = [] } = usePaymentMethodDetails();

  // Mutations para cada módulo
  const createGroupMutation = useCreateGroup();
  const updateGroupMutation = useUpdateGroup();
  const deleteGroupMutation = useDeleteGroup();

  const createAdditionalMutation = useCreateAdditional();
  const updateAdditionalMutation = useUpdateAdditional();
  const deleteAdditionalMutation = useDeleteAdditional();

  const createScoreCoefficientMutation = useAddScoreCoefficient();
  const updateScoreCoefficientMutation = useUpdateScoreCoefficient();
  const deleteScoreCoefficientMutation = useDeleteScoreCoefficient();

  const createPreAdjReasonMutation = useCreatePreAdjudicationReason();
  const updatePreAdjReasonMutation = useUpdatePreAdjudicationReason();
  const deletePreAdjReasonMutation = useDeletePreAdjudicationReason();

  const createConstructionTypeMutation = useCreateConstructionType();
  const updateConstructionTypeMutation = useUpdateConstructionType();
  const deleteConstructionTypeMutation = useDeleteConstructionType();

  const createFeeMutation = useCreateFee();
  const updateFeeMutation = useUpdateFee();
  const deleteFeeMutation = useDeleteFee();

  const createFeeStructureMutation = useCreateFeeStructure();
  const updateFeeStructureMutation = useUpdateFeeStructure();
  const deleteFeeStructureMutation = useDeleteFeeStructure();

  const createPaymentMethodMutation = useCreatePaymentMethod();
  const updatePaymentMethodMutation = useUpdatePaymentMethod();
  const deletePaymentMethodMutation = useDeletePaymentMethod();

  const createPaymentMethodDetailMutation = useCreatePaymentMethodDetail();
  const updatePaymentMethodDetailMutation = useUpdatePaymentMethodDetail();
  const deletePaymentMethodDetailMutation = useDeletePaymentMethodDetail();

  // Filtrar datos por plan actual
  const filteredData = useMemo(() => {
    if (!selectedPlan?.uuid) return {};

    return {
      groups: groups.filter((item) => item.uuid_plan === selectedPlan.uuid),
      additionals: additionals.filter((item) => item.uuid_plan === selectedPlan.uuid),
      scoreCoefficients: scoreCoefficients.filter((item) => item.uuid_plan === selectedPlan.uuid),
      preAdjReasons: preAdjReasons.filter((item) => item.uuid_plan === selectedPlan.uuid),
      constructionTypes: constructionTypes.filter((item) => item.uuid_plan === selectedPlan.uuid),
      fees: fees.filter((item) => item.uuid_plan === selectedPlan.uuid),
      feeStructures: feeStructures.filter((item) => item.uuid_plan === selectedPlan.uuid),
      paymentMethods: paymentMethods.filter((item) => item.uuid_plan === selectedPlan.uuid),
      paymentMethodDetails: paymentMethodDetails.filter(
        (item) => item.uuid_plan === selectedPlan.uuid
      ),
      legalEntities: legalEntities.filter((item) => item.is_intermediate_entity === '1'),
    };
  }, [
    selectedPlan?.uuid,
    groups,
    additionals,
    scoreCoefficients,
    preAdjReasons,
    constructionTypes,
    legalEntities,
    fees,
    feeStructures,
    paymentMethods,
    paymentMethodDetails,
  ]);

  // Handlers genéricos
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Funciones helper para manejar CRUD genérico
  const createCRUDHandlers = (entityType, createMutation, updateMutation, deleteMutation) => ({
    handleAdd: () => {
      setSelectedItems((prev) => ({ ...prev, [entityType]: {} }));
      setEditModes((prev) => ({ ...prev, [entityType]: false }));
      setDialogStates((prev) => ({ ...prev, [entityType]: true }));
    },

    handleEdit: (item) => {
      setSelectedItems((prev) => ({ ...prev, [entityType]: item }));
      setEditModes((prev) => ({ ...prev, [entityType]: true }));
      setDialogStates((prev) => ({ ...prev, [entityType]: true }));
    },

    handleDelete: async (uuid) => {
      try {
        await deleteMutation.mutateAsync(uuid);
      } catch (error) {
        console.error(`Error deleting ${entityType}:`, error);
      }
    },

    handleClose: () => {
      setDialogStates((prev) => ({ ...prev, [entityType]: false }));
      setEditModes((prev) => ({ ...prev, [entityType]: false }));
      setSelectedItems((prev) => ({ ...prev, [entityType]: {} }));
    },

    handleSave: async (data) => {
      try {
        if (!selectedPlan?.uuid) {
          console.error(`Cannot save ${entityType}: no selectedPlan.uuid available`);
          return;
        }

        const entityData = { ...data, uuid_plan: selectedPlan.uuid };

        if (editModes[entityType]) {
          await updateMutation.mutateAsync({
            id: selectedItems[entityType].uuid,
            data: entityData,
          });
        } else {
          await createMutation.mutateAsync(entityData);
        }

        setDialogStates((prev) => ({ ...prev, [entityType]: false }));
        setEditModes((prev) => ({ ...prev, [entityType]: false }));
        setSelectedItems((prev) => ({ ...prev, [entityType]: {} }));
      } catch (error) {
        console.error(`Error saving ${entityType}:`, error);
      }
    },
  });

  // Props para cada tab
  const groupsTabProps = {
    data: filteredData.groups || [],
    selectedItem: selectedItems.group,
    dialogOpen: dialogStates.group,
    editMode: editModes.group,
    ...createCRUDHandlers('group', createGroupMutation, updateGroupMutation, deleteGroupMutation),
  };

  const additionalsTabProps = {
    data: filteredData.additionals || [],
    selectedItem: selectedItems.additional,
    dialogOpen: dialogStates.additional,
    editMode: editModes.additional,
    ...createCRUDHandlers(
      'additional',
      createAdditionalMutation,
      updateAdditionalMutation,
      deleteAdditionalMutation
    ),
  };

  const scoreCoefficientsTabProps = {
    data: filteredData.scoreCoefficients || [],
    selectedItem: selectedItems.scoreCoefficient,
    dialogOpen: dialogStates.scoreCoefficient,
    editMode: editModes.scoreCoefficient,
    ...createCRUDHandlers(
      'scoreCoefficient',
      createScoreCoefficientMutation,
      updateScoreCoefficientMutation,
      deleteScoreCoefficientMutation
    ),
  };

  const preAdjReasonsTabProps = {
    data: filteredData.preAdjReasons || [],
    selectedItem: selectedItems.preAdjReason,
    dialogOpen: dialogStates.preAdjReason,
    editMode: editModes.preAdjReason,
    ...createCRUDHandlers(
      'preAdjReason',
      createPreAdjReasonMutation,
      updatePreAdjReasonMutation,
      deletePreAdjReasonMutation
    ),
  };

  const constructionTypesTabProps = {
    data: filteredData.constructionTypes || [],
    selectedItem: selectedItems.constructionType,
    dialogOpen: dialogStates.constructionType,
    editMode: editModes.constructionType,
    ...createCRUDHandlers(
      'constructionType',
      createConstructionTypeMutation,
      updateConstructionTypeMutation,
      deleteConstructionTypeMutation
    ),
  };

  const peiValuesTabProps = {
    data: filteredData.legalEntities || [],
    selectedPlan,
  };

  const feeTypesTabProps = {
    data: filteredData.fees || [],
    selectedItem: selectedItems.fee,
    dialogOpen: dialogStates.fee,
    editMode: editModes.fee,
    ...createCRUDHandlers('fee', createFeeMutation, updateFeeMutation, deleteFeeMutation),
  };

  const feeStructuresTabProps = {
    data: filteredData.feeStructures || [],
    fees: filteredData.fees || [],
    selectedItem: selectedItems.feeStructure,
    dialogOpen: dialogStates.feeStructure,
    editMode: editModes.feeStructure,
    selectedPlan,
    ...createCRUDHandlers(
      'feeStructure',
      createFeeStructureMutation,
      updateFeeStructureMutation,
      deleteFeeStructureMutation
    ),
  };

  const paymentMethodsTabProps = {
    data: filteredData.paymentMethods || [],
    selectedItem: selectedItems.paymentMethod,
    dialogOpen: dialogStates.paymentMethod,
    editMode: editModes.paymentMethod,
    ...createCRUDHandlers(
      'paymentMethod',
      createPaymentMethodMutation,
      updatePaymentMethodMutation,
      deletePaymentMethodMutation
    ),
  };

  const paymentMethodDetailsTabProps = {
    data: filteredData.paymentMethodDetails || [],
    paymentMethods: filteredData.paymentMethods || [],
    fees: filteredData.fees || [],
    selectedItem: selectedItems.paymentMethodDetail,
    dialogOpen: dialogStates.paymentMethodDetail,
    editMode: editModes.paymentMethodDetail,
    selectedPlan,
    ...createCRUDHandlers(
      'paymentMethodDetail',
      createPaymentMethodDetailMutation,
      updatePaymentMethodDetailMutation,
      deletePaymentMethodDetailMutation
    ),
  };

  return {
    // Estados para cada tab
    groupsTabProps,
    additionalsTabProps,
    scoreCoefficientsTabProps,
    preAdjReasonsTabProps,
    constructionTypesTabProps,
    peiValuesTabProps,
    feeTypesTabProps,
    feeStructuresTabProps,
    paymentMethodsTabProps,
    paymentMethodDetailsTabProps,

    // Funciones de manejo
    handleTabChange,
  };
};
